import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import CommunityResultModal from '../../ChallengeWire/CommunityResultModal';
import CommunityLeaderboard from '../../ChallengeWire/CommunityLeaderboard';
import { challengeCardBtn } from '../../ChallengeWire/ChallengeCard';
import ChallengeStatusIndicator from '../../ChallengeWire/ChallengeStatusIndicator';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchUserFitnessProfiles } from '../../../utils/fitnessProfilesApi';
import {
	cancelCommunityChallenge,
	fetchCommunityChallenge,
	joinCommunityChallenge,
	submitCommunityResult
} from '../../../utils/communityChallengeApi';
import { resolveChallengeWireViewer } from '../../../utils/challengeWireViewer';
import {
	challengeSummaryFooterClass,
	challengeSummarySurfaceClass,
	inputClass,
	pageMutedTextClass,
	sectionLabelClass
} from '../../ChallengeWire/challengeWireFormStyles';
import { buildChallengeScheduleLine } from '../../../utils/challengeSummaryDisplay';

function submitButtonLabel(participation, meta) {
	if (meta?.result_input_type === 'miles') {
		return participation?.result_display ? 'Update total' : 'Set total';
	}
	if (participation?.result_display) {
		return meta.result_entry_mode === 'accumulate_during_window' ? 'Log more' : 'Update time';
	}
	return meta.result_entry_mode === 'accumulate_during_window' ? 'Log entry' : 'Submit time';
}

export default function CommunityChallengeShowPage() {
	const { id } = useParams();
	const navigate = useNavigate();
	const { user, csrfToken, endpoints, activeFitnessProfile } = useAuth();
	const [data, setData] = useState(null);
	const [profiles, setProfiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [joinProfileId, setJoinProfileId] = useState('');
	const [actionLoading, setActionLoading] = useState(false);
	const [resultOpen, setResultOpen] = useState(false);
	const [resultError, setResultError] = useState('');
	const isMounted = useRef(true);

	const viewer = useMemo(
		() => resolveChallengeWireViewer({ activeFitnessProfile, profiles }),
		[activeFitnessProfile, profiles]
	);

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (!user?.id || !id) return undefined;

		let cancelled = false;
		setLoading(true);
		setError('');

		fetchCommunityChallenge({
			userId: user.id,
			challengeId: id,
			endpoint: endpoints.get_user_community_challenge_endpoint,
			csrfToken
		})
			.then((challenge) => {
				if (cancelled || !isMounted.current) return;
				setData(challenge);
			})
			.catch((e) => {
				if (cancelled || !isMounted.current) return;
				setError(e.message);
			})
			.finally(() => {
				if (cancelled || !isMounted.current) return;
				setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [user?.id, id, endpoints, csrfToken]);

	useEffect(() => {
		if (!user?.id) return undefined;

		let cancelled = false;
		fetchUserFitnessProfiles({
			userId: user.id,
			endpoint: endpoints.get_user_fitness_profiles_endpoint,
			csrfToken
		})
			.then((list) => {
				if (cancelled || !isMounted.current) return;
				setProfiles(list);
				const preferred =
					viewer.fitnessProfileId ||
					list.find((p) => p.is_primary)?.id ||
					list[0]?.id;
				if (preferred) setJoinProfileId(String(preferred));
			})
			.catch(() => {
				if (cancelled || !isMounted.current) return;
				setProfiles([]);
			});

		return () => {
			cancelled = true;
		};
	}, [user?.id, endpoints, csrfToken, viewer.fitnessProfileId]);

	async function handleJoin() {
		if (!joinProfileId) return;
		setActionLoading(true);
		setError('');
		try {
			const updated = await joinCommunityChallenge({
				userId: user.id,
				challengeId: id,
				endpoint: endpoints.join_user_community_challenge_endpoint,
				csrfToken,
				fitnessProfileId: joinProfileId
			});
			if (isMounted.current) setData(updated);
		} catch (e) {
			if (isMounted.current) setError(e.message);
		} finally {
			if (isMounted.current) setActionLoading(false);
		}
	}

	async function handleSubmitResult({ resultValue, note }) {
		if (!data?.participation?.id) return;
		setActionLoading(true);
		setResultError('');
		try {
			const updated = await submitCommunityResult({
				userId: user.id,
				challengeId: id,
				participationId: data.participation.id,
				endpoint: endpoints.submit_result_user_community_challenge_participation_endpoint,
				csrfToken,
				resultValue,
				note
			});
			if (isMounted.current) {
				setData(updated);
				setResultOpen(false);
			}
		} catch (e) {
			if (isMounted.current) setResultError(e.message);
		} finally {
			if (isMounted.current) setActionLoading(false);
		}
	}

	async function handleCancel() {
		if (!window.confirm('Cancel this community challenge for everyone?')) return;
		setActionLoading(true);
		try {
			await cancelCommunityChallenge({
				userId: user.id,
				challengeId: id,
				endpoint: endpoints.cancel_user_community_challenge_endpoint,
				csrfToken
			});
			if (isMounted.current) setActionLoading(false);
			navigate('/challenge-wire');
		} catch (e) {
			if (isMounted.current) {
				setError(e.message);
				setActionLoading(false);
			}
		}
	}

	const scheduleLine = data ? buildChallengeScheduleLine(data.starts_on, data.ends_on) : '';
	const permissions = data?.permissions || {};
	const meta = data?.template_meta || {};
	const canLogOrSubmit = permissions.can_submit_result;
	const hasLeaderboard = (data?.leaderboard?.length || 0) > 0;
	const waitingNames = data?.in_progress?.map((p) => p.profile_name) || [];
	const showMetaFooter =
		(data?.early_joiners?.length > 0 && data.early_joiners.length > 1) ||
		waitingNames.length > 0 ||
		(data?.result_logs?.length || 0) > 0;

	return (
		<div className="container mx-auto max-w-xl p-4 pt-4 md:px-8 lg:py-10">
			<Link
				to="/challenge-wire"
				className="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
			>
				← Challenge Wire
			</Link>

			{loading && <p className={`mt-8 ${pageMutedTextClass}`}>Loading…</p>}
			{error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

			{data && !loading && (
				<article
					className={`mt-5 overflow-hidden rounded-xl border border-gray-200 dark:border-dark-border ${challengeSummarySurfaceClass}`}
				>
					<div className="space-y-3 p-5 sm:p-6">
						<div className="flex items-start justify-between gap-3">
							<p className={sectionLabelClass}>Community Challenge</p>
							<ChallengeStatusIndicator status={data.status} />
						</div>

						<div className="space-y-2">
							<h1 className="text-2xl font-bold leading-tight text-gray-900 dark:text-dark-text sm:text-3xl">
								{data.title}
							</h1>
							<p className="text-sm text-gray-600 dark:text-gray-400">
								Posted by{' '}
								<span className="font-medium text-gray-900 dark:text-dark-text">
									{data.creator?.full_name}
								</span>
							</p>
							{scheduleLine && (
								<p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
									{scheduleLine}
								</p>
							)}
							{(data.description || data.personal_note) && (
								<p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
									{data.description || data.personal_note}
								</p>
							)}
							<p className="text-sm text-gray-500 dark:text-gray-400">
								{data.joined_count} joined · {data.eligible?.profile_count} eligible
								{hasLeaderboard ? ` · ${data.leaderboard.length} results` : ''}
							</p>
						</div>
					</div>

					<div className="border-t border-gray-100 bg-gray-50 px-5 py-5 dark:border-dark-border dark:bg-dark-bg sm:px-6">
						<h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-dark-text">Leaderboard</h2>
						<CommunityLeaderboard
							rows={data.leaderboard || []}
							viewerParticipationId={data.participation?.id}
							emptyMessage={
								data.participation
									? 'Submit your result to claim rank #1.'
									: 'No results yet — be the first on the board.'
							}
						/>
					</div>

					{permissions.can_join && (
						<div className={`space-y-3 border-t border-gray-100 px-5 py-4 dark:border-dark-border sm:px-6 ${challengeSummaryFooterClass}`}>
							<p className="text-sm font-medium text-gray-900 dark:text-dark-text">Join this challenge</p>
							{profiles.length > 1 && (
								<select
									className={inputClass}
									value={joinProfileId}
									onChange={(e) => setJoinProfileId(e.target.value)}
								>
									{profiles.map((p) => (
										<option key={p.id} value={p.id}>
											{p.name}
										</option>
									))}
								</select>
							)}
							<button
								type="button"
								onClick={handleJoin}
								disabled={actionLoading}
								className={`${challengeCardBtn.primary} w-full`}
							>
								Join challenge
							</button>
						</div>
					)}

					{data.participation && canLogOrSubmit && (
						<div className={`border-t border-gray-100 px-5 py-4 dark:border-dark-border sm:px-6 ${challengeSummaryFooterClass}`}>
							<button
								type="button"
								onClick={() => setResultOpen(true)}
								disabled={actionLoading}
								className={`${challengeCardBtn.primary} w-full`}
							>
								{submitButtonLabel(data.participation, meta)}
							</button>
						</div>
					)}

					{showMetaFooter && (
						<div className="space-y-2 border-t border-gray-100 px-5 py-4 text-xs leading-relaxed text-gray-500 dark:border-dark-border dark:text-gray-400 sm:px-6">
							{data.early_joiners?.length > 1 && (
								<p>
									<span className="font-medium text-gray-600 dark:text-gray-300">Early joiners · </span>
									{data.early_joiners.join(', ')}
								</p>
							)}
							{waitingNames.length > 0 && (
								<p>
									<span className="font-medium text-gray-600 dark:text-gray-300">Waiting on results · </span>
									{waitingNames.join(', ')}
								</p>
							)}
							{data.result_logs?.length > 0 && (
								<ul className="space-y-1">
									{data.result_logs.map((log, i) => (
										<li key={`${log.logged_at}-${i}`}>
											+{log.value} · {new Date(log.logged_at).toLocaleString()}
											{log.note ? ` · ${log.note}` : ''}
										</li>
									))}
								</ul>
							)}
						</div>
					)}

					{permissions.can_cancel && (
						<div className="border-t border-gray-100 px-5 py-3 text-center dark:border-dark-border sm:px-6">
							<button
								type="button"
								onClick={handleCancel}
								disabled={actionLoading}
								className="text-sm text-red-600 dark:text-red-400 hover:underline"
							>
								Cancel challenge
							</button>
						</div>
					)}
				</article>
			)}

			<CommunityResultModal
				isOpen={resultOpen}
				onClose={() => setResultOpen(false)}
				onConfirm={handleSubmitResult}
				templateMeta={meta}
				currentBest={data?.participation?.result_display}
				currentResultValue={data?.participation?.result_value}
				loading={actionLoading}
				error={resultError}
			/>
		</div>
	);
}
